/**
 * The one sentence an invoice DOCUMENT prints about the card service fee.
 *
 * Shared by all three invoice renderers — the classic PDF, the templated PDF
 * and its DOM twin — so a customer reading the hosted page and the downloaded
 * PDF can never be told two different things about the same money.
 *
 * Two facts it exists to keep straight:
 *
 *  - **The fee is history, not a charge on this invoice.** It was taken on top
 *    of a payment that has already happened; the totals block above it must
 *    not move. Hence the past tense and the placement under the totals.
 *  - **A deposit fee and a payment fee are different money.** An invoice can
 *    carry both (a card deposit, then the balance paid by card), and the two
 *    are separate figures server-side precisely so the sentence can say which
 *    is which instead of printing one unexplained lump.
 *
 * Wording follows #2877: the card price is the number of record and the
 * ACH/check price is described as the saving that was not taken. Matches the
 * estimate documents' "Card price used; $X ACH/check savings not applied."
 */
export function invoiceServiceFeeNote(opts: {
  /** Fee charged on the deposits behind the invoice's deposit credit. */
  depositServiceFee: number;
  /** Fee charged on money paid against the invoice itself. */
  paymentServiceFee: number;
  /** The document's own currency formatter, so the sentence matches its totals. */
  fmt: (amount: number) => string;
}): string | null {
  const deposit = opts.depositServiceFee > 0 ? opts.depositServiceFee : 0;
  const payment = opts.paymentServiceFee > 0 ? opts.paymentServiceFee : 0;
  if (deposit === 0 && payment === 0) return null;

  if (deposit > 0 && payment === 0) {
    return `Card price used on the deposit; ${opts.fmt(deposit)} ACH/check savings not applied.`;
  }
  if (payment > 0 && deposit === 0) {
    return `Card price used; ${opts.fmt(payment)} ACH/check savings not applied.`;
  }
  // Both. State the total — it is what left the customer's card — and name the
  // deposit share, so the figure reconciles against the deposit row above.
  return `Card price used; ${opts.fmt(deposit + payment)} ACH/check savings not applied (${opts.fmt(
    deposit,
  )} of it on the deposit).`;
}
